import * as React from "react";
import { graphql } from "gatsby";
import "../gatsby/fragments";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import PressCard from "../components/press/press-card-compact";
import IframeResizer from "iframe-resizer-react";
import Page, { PageHead } from "../components/layout/page";
import PageHeader from "../components/layout/page-header";

function PressImage({ image, alt, title }) {
  const imageData = getImage(image);
  return !imageData ? null : (
    <div className="d-flex justify-content-around">
      <GatsbyImage image={imageData} alt={alt} title={title} />
    </div>
  );
}

function SmallIframe({ src, ...props }) {
  return (
    <div className="container" {...props}>
      <IframeResizer
        log
        src={src}
        style={{ width: "1px", minWidth: "100%", minHeight: "100px" }}
      />
    </div>
  );
}

function FullWidthIframe({ src, ...props }) {
  return (
    <div className="my-3">
      <div className="container">
        <div
          className="ratio ratio-16x9"
          style={{ maxHeight: "90vh" }}
          {...props}
        >
          <IframeResizer
            log
            src={src}
            style={{ width: "1px", minWidth: "100%", minHeight: "100px" }}
          />
        </div>
      </div>
    </div>
  );
}

function PrevNextPressCards({ previous, next }) {
  const options = { stretchMainLink: true, showSecondaryLinks: false };
  return (
    <div className="row py-3 py-lg-5">
      <div className="col-12 col-lg-8 offset-lg-3">
        <div className="row justify-content-between">
          {!previous ? null : (
            <div className="col col-6 col-md-5 col-xl-4 d-none d-sm-block">
              <PressCard
                item={previous}
                options={{ mainLinkText: "← Previous", ...options }}
              />
            </div>
          )}
          {!next ? null : (
            <div className="col col-12 col-sm-6 col-md-5 col-xl-4">
              <PressCard
                item={next}
                options={{ mainLinkText: "Next →", ...options }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Movie({ publicURL, extension }) {
  const types = {
    mov: "video/mp4",
    mp4: "video/mp4",
  };
  return (
    <div className="container">
      <video controls width="100%">
        <source src={publicURL} type={types[extension]} />
      </video>
    </div>
  );
}

export default function PressItemTemplate({ data, ...props }) {
  const item = data.press;
  const frontmatter = { ...data.press };
  frontmatter.tagline = frontmatter.venue;
  frontmatter.lead = frontmatter.description;
  frontmatter.peopleLabel = "Featuring";
  const links = item.links?.slice() || [];

  // Add pdf download links
  if (item.media) {
    item?.media?.forEach((media) => {
      if (media?.file?.extension == "pdf") {
        links.push({
          href: media.file.publicURL,
          text: `${media.title || "Download"} (PDF) ↓`,
        });
      }
    });
  }

  // Link to original link
  if (item.archive_url) {
    links.push({
      href: item.archive_url,
      text: "Archive",
      className: "btn btn-outline-light opacity-50",
    });
  }

  frontmatter.links = [...links];
  delete frontmatter.fields;

  return (
    <Page frontmatter={frontmatter} empty={true} {...props}>
      <PageHeader {...frontmatter} />

      {item.iframes?.map((iframe) => {
        return item.item_type === "video" || iframe?.fullwidth === true ? (
          <FullWidthIframe src={iframe.src} key={iframe.src} />
        ) : (
          <SmallIframe src={iframe.src} key={iframe.src} />
        );
      })}

      {item.images?.map((image) => {
        return (
          <PressImage
            title={image.title}
            alt={image.alt || item.title}
            image={image.src || item.title}
          />
        );
      })}

      {item.media?.map((media) => {
        const ext = media?.file?.extension;
        if (ext === "mov" || ext === "mp4") {
          return <Movie {...media.file} />;
        } else if (ext === "mp3") {
          return (
            <div className="container">
              <div className="alert alert-danger">
                Media type "mp3" is not yet supported.
              </div>
            </div>
          );
        } else if (ext !== "pdf") {
          console.warn(
            `Cannot show media of "${frontmatter.title}" (${frontmatter.date}). Received data:`,
            media
          );
          // Pdfs are shown as links
          return (
            <div className="container">
              <div className="alert alert-danger">
                {ext
                  ? `Media type "${ext}" is not supported.`
                  : "Media file missing"}
              </div>
            </div>
          );
        }
      })}

      <div className="container">
        <PrevNextPressCards previous={data.previous} next={data.next} />
      </div>
    </Page>
  );
}

export const Head = ({ data }) => <PageHead title={data.press.title} />;

export const pageQuery = graphql`
  query ($id: String!, $nextId: String, $previousId: String) {
    press(id: { eq: $id }) {
      ...pressInfo
      media {
        title
        file: src {
          publicURL
          extension
        }
      }
      images {
        alt
        title
        src {
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED
              transformOptions: { fit: CONTAIN }
              height: 1000
            )
          }
        }
      }
      iframes {
        src
        fullwidth
      }
    }

    previous: press(id: { eq: $previousId }) {
      ...pressInfo
    }

    next: press(id: { eq: $nextId }) {
      ...pressInfo
    }
  }
`;
